import { useState } from 'react';
import { toast } from 'sonner';
import { Upload } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { saveProduct } from '../api/use-products';
import { parseProductsCsv, type ParsedProductRow, type ProductCsvParseError } from '../model/product-csv';

interface ProductImportDialogProps {
  idPassbook: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImported: () => void;
}

type RowResult = { line: number; name: string; ok: boolean; message?: string };

export function ProductImportDialog({ idPassbook, open, onOpenChange, onImported }: ProductImportDialogProps) {
  const [fileName, setFileName] = useState<string | null>(null);
  const [rows, setRows] = useState<ParsedProductRow[]>([]);
  const [parseErrors, setParseErrors] = useState<ProductCsvParseError[]>([]);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState<RowResult[]>([]);

  const reset = () => {
    setFileName(null);
    setRows([]);
    setParseErrors([]);
    setImporting(false);
    setProgress(0);
    setResults([]);
  };

  const onFile = async (file: File) => {
    setFileName(file.name);
    setResults([]);
    const text = await file.text();
    const parsed = parseProductsCsv(text);
    setRows(parsed.rows);
    setParseErrors(parsed.errors);
  };

  const runImport = async () => {
    if (rows.length === 0) return;
    setImporting(true);
    setProgress(0);
    const out: RowResult[] = [];
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      try {
        await saveProduct({ action: 'insert', idPassbook, ...row.payload });
        out.push({ line: row.line, name: row.payload.name, ok: true });
      } catch (err) {
        out.push({
          line: row.line,
          name: row.payload.name,
          ok: false,
          message: err instanceof Error ? err.message : 'Falha ao importar.',
        });
      }
      setProgress(i + 1);
    }
    setResults(out);
    setImporting(false);
    const okCount = out.filter((r) => r.ok).length;
    if (okCount > 0) {
      toast.success(`${okCount} produto${okCount > 1 ? 's' : ''} importado${okCount > 1 ? 's' : ''}.`);
      onImported();
    }
    if (okCount < out.length) {
      toast.error(`${out.length - okCount} linha(s) falharam ao importar.`);
    }
  };

  const retryFailures = () => {
    const failedLines = new Set(results.filter((r) => !r.ok).map((r) => r.line));
    setRows((prev) => prev.filter((r) => failedLines.has(r.line)));
    setResults([]);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Importar produtos (CSV)</DialogTitle>
          <DialogDescription>
            Use o modelo com as colunas sku, nome, valor, preco, resgate, tipo_resgate.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="products-csv-input">Arquivo CSV</Label>
            <Input
              id="products-csv-input"
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void onFile(file);
              }}
            />
            {fileName ? <p className="text-xs text-muted-foreground">{fileName}</p> : null}
          </div>

          {rows.length > 0 || parseErrors.length > 0 ? (
            <div className="space-y-1 text-sm">
              <p>{rows.length} linha(s) válida(s) prontas para importar.</p>
              {parseErrors.length > 0 ? (
                <div className="max-h-32 space-y-0.5 overflow-y-auto rounded-lg border border-destructive/30 bg-destructive/5 p-2 text-xs text-destructive">
                  {parseErrors.map((e) => (
                    <p key={e.line}>
                      Linha {e.line}: {e.message}
                    </p>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}

          {importing ? (
            <p className="text-sm text-muted-foreground">
              Importando {progress}/{rows.length}…
            </p>
          ) : null}

          {results.length > 0 ? (
            <div className="max-h-40 space-y-0.5 overflow-y-auto rounded-lg border border-border p-2 text-xs">
              {results.map((r) => (
                <p key={r.line} className={r.ok ? 'text-success' : 'text-destructive'}>
                  Linha {r.line} — {r.name || '(sem nome)'}: {r.ok ? 'importado' : r.message}
                </p>
              ))}
              {results.some((r) => !r.ok) ? (
                <Button variant="secondary" className="mt-2" onClick={retryFailures}>
                  Repetir só as falhas
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
          <Button type="button" onClick={() => void runImport()} loading={importing} disabled={rows.length === 0}>
            <Upload className="size-4" /> Importar {rows.length > 0 ? `(${rows.length})` : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
